/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import styled from "styled-components";
import { FlexContainer, SVGIcon, theme, Tooltip } from "@nordcloud/gnui";
import { SelectableBox } from "~/components/SelectableBox";
import { TooltipWrapper } from "~/components/Styled";
import { getKpiType, KpiType } from "~/constants";
import { Maybe, noop } from "~/tools";
import { useKpiWidget } from "../hooks";
import { KpiSummary } from "./KpiSummary";
import { KpiSectionTitle } from "./SectionTitle";

type Props = {
  value: Maybe<string>;
  target: Maybe<number>;
  type: KpiType;
  additionalValue?: Maybe<string>;
  selected?: boolean;
  onClick?: (type: KpiType) => void;
};

export function KpiWidget({
  value,
  target,
  type,
  additionalValue,
  onClick = noop,
  selected = false,
}: Props) {
  const { label, iconWrapperColor, icon, items } = useKpiWidget({
    value,
    target,
    type,
    additionalValue,
  });

  const { description } = getKpiType(type);

  return (
    <SelectableBox selected={selected} onClick={() => onClick(type)}>
      <KpiWidgetGrid data-testid="kpi-widget-content">
        <IconWrapper
          css={{ backgroundColor: iconWrapperColor }}
          justifyContent="center"
          data-testid="kpi-icon-wrapper"
        >
          <SVGIcon name={icon} color="white" size="sm" />
        </IconWrapper>
        <Title>
          <KpiSectionTitle title={label} />
        </Title>
        <Info>
          <TooltipWrapper>
            <Tooltip caption={description}>
              <FlexContainer>
                <SVGIcon name="info" size="smd" />
              </FlexContainer>
            </Tooltip>
          </TooltipWrapper>
        </Info>
        <Summary>
          <KpiSummary items={items} />
        </Summary>
      </KpiWidgetGrid>
    </SelectableBox>
  );
}

const IconWrapper = styled(FlexContainer)`
  border-radius: ${theme.radiusDefault};
  width: 2rem;
  height: 1.5rem;
`;

const KpiWidgetGrid = styled.div`
  display: grid;
  width: 100%;
  align-items: center;
  column-gap: ${theme.spacing.spacing02};
  grid-template-columns: 2rem minmax(0, 1fr) 1rem;
  grid-template-rows: 1fr 2fr;
  grid-template-areas:
    "icon title info"
    ". summary summary";
`;

const Summary = styled.div`
  grid-area: summary;
`;

const Title = styled.div`
  grid-area: title;

  p {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
`;

const Info = styled.div`
  grid-area: info;
  justify-self: center;
`;
